import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";

const PHONE = "504-733-0871";
const MAPS_LINK =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent("Palmisano Family Dentistry, 2020 Dickory Ave Suite 104, River Ridge, LA 70123");

const MENUS = [
  {
    label: "Practice",
    items: [
      { label: "Our Philosophy", to: "/", hash: "#philosophy" },
      { label: "Meet the Family", to: "/", hash: "#practice" },
      { label: "New Patients", href: `tel:${PHONE.replace(/-/g, "")}` },
    ],
  },
  {
    label: "Services",
    items: [
      { label: "Preventive & General", to: "/services", hash: "#svc-01" },
      { label: "Restorative", to: "/services", hash: "#svc-02" },
      { label: "Cosmetic", to: "/services", hash: "#svc-03" },
      { label: "Oral Surgery", to: "/services", hash: "#svc-04" },
      { label: "Appliances & TMJ", to: "/services", hash: "#svc-05" },
      { label: "Technology & Comfort", to: "/services", hash: "#svc-06" },
      { label: "View all services", to: "/services", hash: "" },
    ],
  },
  {
    label: "Contact",
    items: [
      { label: "Call the Office", href: `tel:${PHONE.replace(/-/g, "")}` },
      { label: "Email Us", href: "mailto:palmisanodental@gmail.com" },
      { label: "Get Directions", href: MAPS_LINK, external: true },
      { label: "Hours & Location", to: "/", hash: "#contact" },
    ],
  },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(null);

  const goTo = (item) => {
    setOpen(null);
    const target = item.to + (item.hash || "");
    if (item.to === location.pathname) {
      if (item.hash) {
        const el = document.querySelector(item.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      navigate(target);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F6F0]/70 border-b border-[#2A2E26]/10"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-display text-xl sm:text-2xl tracking-tight text-[#2A2E26]">
          Palmisano<span className="text-[#C05C42]">.</span>
        </Link>

        <div className="hidden md:flex items-center gap-2">
          {MENUS.map((menu) => (
            <div
              key={menu.label}
              className="relative"
              onMouseEnter={() => setOpen(menu.label)}
              onMouseLeave={() => setOpen((o) => (o === menu.label ? null : o))}
            >
              <button
                onClick={() => setOpen((o) => (o === menu.label ? null : menu.label))}
                data-testid={`nav-${menu.label.toLowerCase()}`}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#4A4E46] hover:text-[#C05C42] transition-colors duration-300"
              >
                {menu.label}
                <ChevronDown size={15} className={`transition-transform duration-300 ${open === menu.label ? "rotate-180 text-[#C05C42]" : ""}`} />
              </button>

              <AnimatePresence>
                {open === menu.label && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`dropdown-${menu.label.toLowerCase()}`}
                    className="absolute left-0 top-full pt-3 w-60"
                  >
                    <div className="bg-[#F9F6F0] border border-[#2A2E26]/10 rounded-2xl shadow-xl p-2 overflow-hidden">
                      {menu.items.map((item) =>
                        item.href ? (
                          <a
                            key={item.label}
                            href={item.href}
                            target={item.external ? "_blank" : undefined}
                            rel={item.external ? "noopener noreferrer" : undefined}
                            onClick={() => setOpen(null)}
                            data-testid={`dropdown-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="block px-4 py-2.5 rounded-xl text-sm font-medium text-[#2A2E26] hover:bg-[#F0EBE1] hover:text-[#C05C42] transition-colors duration-200"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <button
                            key={item.label}
                            onClick={() => goTo(item)}
                            data-testid={`dropdown-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="block w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium text-[#2A2E26] hover:bg-[#F0EBE1] hover:text-[#C05C42] transition-colors duration-200"
                          >
                            {item.label}
                          </button>
                        )
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <a href={`tel:${PHONE.replace(/-/g, "")}`} data-testid="nav-call-btn"
          className="btn-terra text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2">
          <Phone size={15} /> <span className="hidden sm:inline">{PHONE}</span><span className="sm:hidden">Call</span>
        </a>
      </nav>
    </motion.header>
  );
}
